import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ProgressSpinner} from 'primereact/progressspinner';
import {Dialog} from 'primereact/dialog';
import {InputText} from 'primereact/inputtext';
import {Button} from 'primereact/button';
import {ApiProject, useGetProjectsQuery, useGetDrillholesQuery, useRenameProjectMutation} from '../redux/rtk/drillholeApi';
import {setSelectedProject} from '../redux/slices/ProjectSlice';
import {
    setSelectedBoreholes,
    setSelectLine,
    setSelectedDrillholeId,
} from '../redux/slices/ChartStateSlice';
import {RootState} from '../redux/store';
import {navigate} from '../functions/urlHash';

const PER_PROJECT_LOCAL_KEYS = [
    'boreholes3DSelectedDrillholes',
    'coreViewerSelectedDrillholes',
    'selectedDrillholeId',
    'line',
];

type ProjectCardProps = {
    project: ApiProject;
    onSelect: (project: ApiProject) => void;
    onRename: (e: React.MouseEvent, project: ApiProject) => void;
};

function ProjectCard({project, onSelect, onRename}: ProjectCardProps) {
    const {data: drillholes, isLoading, error} = useGetDrillholesQuery(project.project_id);

    const holeIds: string[] = (drillholes || [])
        .map((d: any) => d.hole_id ?? d.id)
        .filter((v: any): v is string => typeof v === 'string' && v.length > 0);

    return (
        <div
            onClick={() => onSelect(project)}
            className="p-4 cursor-pointer surface-card border-round"
            style={{
                position: 'relative',
                border: '1px solid var(--surface-border)',
                transition: 'transform 120ms ease, box-shadow 120ms ease',
            }}
            onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
            }}
            onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.transform = '';
                (e.currentTarget as HTMLDivElement).style.boxShadow = '';
            }}
        >
            <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-text p-button-sm"
                tooltip="Rename project"
                tooltipOptions={{position: 'top'}}
                style={{position: 'absolute', top: 4, right: 4}}
                onClick={(e) => onRename(e, project)}
                aria-label="Rename project"
            />
            <div style={{fontSize: 18, fontWeight: 600, paddingRight: 32}}>{project.name}</div>
            <div style={{color: 'var(--text-color-secondary)', fontSize: 12, marginTop: 4}}>
                {project.project_id}
            </div>

            <div style={{marginTop: 12, borderTop: '1px solid var(--surface-border)', paddingTop: 8}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-color-secondary)', fontSize: 12, marginBottom: 6}}>
                    <i className="pi pi-bullseye" style={{fontSize: 12}}/>
                    {isLoading && <span>Loading drillholes…</span>}
                    {error && <span style={{color: 'var(--red-500)'}}>Failed to load drillholes</span>}
                    {!isLoading && !error && (
                        <span>{holeIds.length} drillhole{holeIds.length === 1 ? '' : 's'}</span>
                    )}
                </div>
                {!isLoading && !error && holeIds.length > 0 && (
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: 4}}>
                        {holeIds.map((id) => (
                            <span
                                key={id}
                                className="surface-100 border-round"
                                style={{
                                    fontSize: 11,
                                    padding: '2px 6px',
                                    border: '1px solid var(--surface-border)',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {id}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function ProjectOverview() {
    const dispatch = useDispatch();
    const currentProjectId = useSelector((state: RootState) => state.project.selectedProject?.project_id);
    const {data: projects, isLoading, error} = useGetProjectsQuery();
    const [renameProject, {isLoading: isRenaming}] = useRenameProjectMutation();

    const [renameTarget, setRenameTarget] = useState<ApiProject | null>(null);
    const [renameValue, setRenameValue] = useState<string>('');
    const [renameError, setRenameError] = useState<string | null>(null);

    const onSelect = (project: ApiProject) => {
        if (currentProjectId !== project.project_id) {
            // Projektwechsel: drillhole-bezogenen State leeren, damit nichts vom
            // vorigen Projekt nachzieht.
            dispatch(setSelectedBoreholes([]));
            dispatch(setSelectLine(undefined));
            dispatch(setSelectedDrillholeId(undefined));
            PER_PROJECT_LOCAL_KEYS.forEach(k => localStorage.removeItem(k));
        }
        // Name direkt setzen, damit er nicht erst beim URL-Sync auf den ID-Fallback fällt
        dispatch(setSelectedProject({project_id: project.project_id, name: project.name}));
        navigate({view: 'geomap', projectId: project.project_id});
    };

    const openRename = (e: React.MouseEvent, project: ApiProject) => {
        e.stopPropagation();
        setRenameTarget(project);
        setRenameValue(project.name);
        setRenameError(null);
    };

    const closeRename = () => {
        setRenameTarget(null);
        setRenameValue('');
        setRenameError(null);
    };

    const submitRename = async () => {
        if (!renameTarget) return;
        const trimmed = renameValue.trim();
        if (trimmed === '' || trimmed === renameTarget.name) {
            closeRename();
            return;
        }
        try {
            await renameProject({projectId: renameTarget.project_id, name: trimmed}).unwrap();
            if (currentProjectId === renameTarget.project_id) {
                dispatch(setSelectedProject({project_id: renameTarget.project_id, name: trimmed}));
            }
            closeRename();
        } catch (err: any) {
            const msg = err?.data?.error || err?.error || 'Rename failed';
            setRenameError(String(msg));
        }
    };

    return (
        <div className="card w-full h-full p-4">
            <h2 className="m-0 mb-3">Projects</h2>
            {isLoading && (
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh'}}>
                    <ProgressSpinner/>
                </div>
            )}
            {error && (
                <div style={{color: 'var(--red-500)'}}>Failed to load projects.</div>
            )}
            {!isLoading && !error && projects && projects.length === 0 && (
                <div style={{color: '#999'}}>No projects available.</div>
            )}
            {!isLoading && !error && projects && projects.length > 0 && (
                <div className="grid">
                    {projects.map((p) => (
                        <div key={p.project_id} className="col-12 sm:col-6 md:col-4 lg:col-3">
                            <ProjectCard project={p} onSelect={onSelect} onRename={openRename}/>
                        </div>
                    ))}
                </div>
            )}

            <Dialog
                header="Rename project"
                visible={renameTarget !== null}
                onHide={closeRename}
                style={{width: '32rem'}}
                modal
                footer={(
                    <div>
                        <Button
                            label="Cancel"
                            className="p-button-text"
                            onClick={closeRename}
                            disabled={isRenaming}
                        />
                        <Button
                            label="Save"
                            icon="pi pi-check"
                            onClick={submitRename}
                            loading={isRenaming}
                            disabled={renameValue.trim() === '' || renameValue.trim() === renameTarget?.name}
                        />
                    </div>
                )}
            >
                <div className="p-fluid">
                    <label htmlFor="renameProjectInput" style={{display: 'block', marginBottom: 6}}>Name</label>
                    <InputText
                        id="renameProjectInput"
                        value={renameValue}
                        autoFocus
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') submitRename();
                        }}
                    />
                    {renameError && (
                        <small style={{color: 'var(--red-500)', display: 'block', marginTop: 6}}>{renameError}</small>
                    )}
                </div>
            </Dialog>
        </div>
    );
}
